/**
 * Rows for the 4D Activities grid: the macro programme, with each discipline
 * schedule (engineering, procurement, fabrication & construction,
 * pre-commissioning & commissioning) nested under the macro activity it
 * delivers, and a date filter that follows the timeliner playhead.
 */

export const DISCIPLINE_META = {
  ENGINEERING: { short: 'ENG', label: 'Engineering', color: '#6366F1' },
  PROCUREMENT: { short: 'PROC', label: 'Procurement', color: '#0EA5E9' },
  FABRICATION_CONSTRUCTION: { short: 'F&C', label: 'Fabrication & Construction', color: '#F97316' },
  PRECOMM_COMMISSIONING: { short: 'PC&C', label: 'Pre-comm & Commissioning', color: '#A855F7' },
};

export const HEALTH_COLORS = {
  // the 10 macro-schedule statuses
  COMPLETED_ON_TIME: '#10B981', COMPLETED_LATE: '#F59E0B', ON_TRACK: '#3B82F6',
  AT_RISK: '#EAB308', DELAYED: '#FB923C', OVERDUE: '#EF4444',
  LATE_START: '#F97316', FORECAST_DELAY: '#FBBF24',
  NOT_STARTED_OVERDUE: '#DC2626', NOT_STARTED: '#94A3B8',
  // discipline (micro) health bands
  COMPLETED: '#10B981', COMPLETE: '#10B981', AHEAD: '#22C55E',
  BEHIND: '#F59E0B', CRITICAL: '#EF4444', IN_PROGRESS: '#3B82F6',
};

const day = (v) => (v ? String(v).slice(0, 10) : null);
const daysBetween = (a, b) =>
  Math.round((new Date(`${a}T00:00:00Z`) - new Date(`${b}T00:00:00Z`)) / 86400000);

const codeParts = (code) => {
  const parts = String(code || '').split('.').filter(Boolean);
  return parts.map((_, i) => parts.slice(0, i + 1).join('.'));
};

/** Health band for a macro activity, from the same fields the 3D legend uses. */
export const macroHealth = (m) => {
  if (m?.scheduleStatus) return m.scheduleStatus === 'COMPLETE' ? 'COMPLETED' : m.scheduleStatus;
  return 'NOT_STARTED';
};

/** Macro rows + micro rows, each carrying the AG Grid tree path. */
export function buildGridRows({ mappings = [], microLinks = [], showMicro = true,
                               disciplines = null }) {
  const macroByCode = new Map();
  const rows = (mappings || []).map((m) => {
    const parts = codeParts(m.activityCode);
    const path = parts.length ? parts : [m.activityId];
    macroByCode.set(m.activityId, { code: m.activityCode || m.activityId, path });
    return {
      ...m,
      _path: path,
      _kind: 'MACRO',
      _health: macroHealth(m),
      _start: day(m.actualStart || m.plannedStart),
      _finish: day(m.actualFinish || m.plannedFinish),
    };
  });
  if (!showMicro) return rows;

  for (const link of microLinks) {
    if (disciplines && disciplines.size && !disciplines.has(link.discipline)) continue;
    const parent = macroByCode.get(link.macroActivityId);
    if (!parent) continue;
    rows.push({
      ...link,
      activityId: `micro:${link.linkId}`,
      _path: [...parent.path, `${parent.code}#${link.linkId}`],
      _kind: 'MICRO',
      _health: link.health,
      _start: day(link.actualStart || link.plannedStart),
      _finish: day(link.actualFinish || link.plannedFinish),
      // reuse the macro column fields so one column set renders both
      plannedStart: link.plannedStart,
      plannedFinish: link.plannedFinish,
      physicalProgressPct: link.progressPct,
      scheduleStatus: link.health,
      isSummary: false,
    });
  }
  return rows;
}

/**
 * Applicable on a given date: live in its own window, or — for a row with only
 * a due date, such as an engineering deliverable — due within a week of it.
 * `null` means the row carries no dates and should follow its macro parent.
 */
export function isApplicable(row, date) {
  const s = row._start;
  const f = row._finish;
  if (s && f) return s <= date && date <= f;
  if (f) return Math.abs(daysBetween(f, date)) <= 7;
  if (s) return s <= date;
  return null;
}

/**
 * Roll the per-macro discipline split up the WBS, so a summary row shows the
 * same E / P / F&C / PC view as the tasks underneath it.
 */
export function aggregateRollup(mappings = [], rollup = {}) {
  const out = { ...rollup };
  const summaries = mappings.filter(m => m.isSummary && m.activityCode);
  for (const s of summaries) {
    const prefix = `${s.activityCode}.`;
    const kids = mappings.filter(m => !m.isSummary
      && String(m.activityCode || '').startsWith(prefix) && rollup[m.activityId]);
    if (!kids.length) continue;
    const merged = {};
    for (const k of kids) {
      for (const [disc, cell] of Object.entries(rollup[k.activityId])) {
        const acc = merged[disc] || (merged[disc] = { pct: 0, count: 0, health: 'COMPLETED' });
        acc.pct += cell.pct * cell.count;
        acc.count += cell.count;
        const order = ['CRITICAL', 'BEHIND', 'NOT_STARTED', 'ON_TRACK', 'AHEAD', 'COMPLETED'];
        if (order.indexOf(cell.health) < order.indexOf(acc.health)) acc.health = cell.health;
      }
    }
    for (const cell of Object.values(merged)) {
      cell.pct = Math.round((cell.pct / Math.max(1, cell.count)) * 10) / 10;
    }
    out[s.activityId] = merged;
  }
  return out;
}

/** Add back the WBS rows a filtered set still needs to render as a tree. */
export function withAncestors(rows, kept) {
  const needed = new Set();
  for (const r of kept) {
    for (let i = 1; i < r._path.length; i += 1) needed.add(r._path.slice(0, i).join('/'));
  }
  const keptIds = new Set(kept.map(r => r.activityId));
  const ancestors = rows.filter(r => !keptIds.has(r.activityId)
                                  && needed.has(r._path.join('/')));
  return [...ancestors, ...kept];
}

/** Keep what is live on `date`, plus the WBS parents needed to render them. */export function filterToDate(rows, date) {
  if (!date) return rows;
  const keptMacroPaths = new Set();
  const kept = [];
  for (const r of rows) {
    if (r._kind !== 'MACRO' || r.isSummary) continue;
    if (isApplicable(r, date) === true) {
      kept.push(r);
      keptMacroPaths.add(r._path.join('/'));
    }
  }
  for (const r of rows) {
    if (r._kind !== 'MICRO') continue;
    const own = isApplicable(r, date);
    const parentPath = r._path.slice(0, -1).join('/');
    if (own === true || (own === null && keptMacroPaths.has(parentPath))) kept.push(r);
  }
  // Pull in the ancestor rows so the tree still has its branches.
  return withAncestors(rows, kept);
}
